import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

// Types
export interface ActivityLog {
  id: string
  site_domain: string
  event_type: string
  category: 'auth' | 'user' | 'plugin' | 'theme' | 'option' | 'post'
  user_email: string | null
  user_name: string | null
  object_type: string | null
  object_name: string | null
  details: string | null // JSON string
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

export interface ActivityLogsResponse {
  logs: ActivityLog[]
  total: number
  limit: number
  offset: number
}

export interface ActivityLogsStats {
  total_events: number
  events_last_24h: number
  by_category: { category: string; count: number }[]
  by_site: { site_domain: string; count: number }[]
  top_events: { event_type: string; count: number }[]
}

export interface ActivityLogsFilters {
  site?: string
  category?: string
  event_type?: string
  user_email?: string
  from?: string
  to?: string
  limit?: number
  offset?: number
}

// Query keys
export const activityLogKeys = {
  all: ['activity-logs'] as const,
  lists: () => [...activityLogKeys.all, 'list'] as const,
  list: (filters: ActivityLogsFilters) => [...activityLogKeys.lists(), filters] as const,
  stats: () => [...activityLogKeys.all, 'stats'] as const,
  statsFiltered: (filters: Pick<ActivityLogsFilters, 'site' | 'from' | 'to'>) =>
    [...activityLogKeys.stats(), filters] as const,
}

// Build query string from filters
function buildQueryString(filters: ActivityLogsFilters): string {
  const params = new URLSearchParams()

  if (filters.site) params.set('site', filters.site)
  if (filters.category) params.set('category', filters.category)
  if (filters.event_type) params.set('event_type', filters.event_type)
  if (filters.user_email) params.set('user_email', filters.user_email)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.limit) params.set('limit', filters.limit.toString())
  if (filters.offset) params.set('offset', filters.offset.toString())

  const query = params.toString()
  return query ? `?${query}` : ''
}

// Hooks
export function useActivityLogs(filters: ActivityLogsFilters = {}) {
  return useQuery({
    queryKey: activityLogKeys.list(filters),
    queryFn: async () => {
      const queryString = buildQueryString(filters)
      return apiClient.get<ActivityLogsResponse>(`/api/auth-server/activity-logs${queryString}`)
    },
  })
}

export function useActivityLogsStats(filters: Pick<ActivityLogsFilters, 'site' | 'from' | 'to'> = {}) {
  return useQuery({
    queryKey: activityLogKeys.statsFiltered(filters),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.site) params.set('site', filters.site)
      if (filters.from) params.set('from', filters.from)
      if (filters.to) params.set('to', filters.to)
      const queryString = params.toString()
      return apiClient.get<ActivityLogsStats>(
        `/api/auth-server/activity-logs/stats${queryString ? `?${queryString}` : ''}`
      )
    },
  })
}

// Helper to parse details JSON
export function parseLogDetails(details: string | null): Record<string, unknown> | null {
  if (!details) return null
  try {
    return JSON.parse(details)
  } catch {
    return null
  }
}

// Event type display names
export const EVENT_TYPE_LABELS: Record<string, string> = {
  // Auth
  login_success: 'Login Success',
  login_failed: 'Login Failed',
  logout: 'Logout',
  // User
  user_created: 'User Created',
  user_deleted: 'User Deleted',
  user_role_changed: 'User Role Changed',
  user_email_changed: 'User Email Changed',
  // Plugin
  plugin_activated: 'Plugin Activated',
  plugin_deactivated: 'Plugin Deactivated',
  plugin_updated: 'Plugin Updated',
  plugin_installed: 'Plugin Installed',
  plugin_deleted: 'Plugin Deleted',
  // Theme
  theme_switched: 'Theme Switched',
  theme_updated: 'Theme Updated',
  // Option
  option_updated: 'Option Updated',
  // Post
  post_trashed: 'Post Trashed',
  post_deleted: 'Post Deleted',
}

// Category display names and colors
export const CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
  auth: { label: 'Auth', color: 'bg-blue-500' },
  user: { label: 'User', color: 'bg-purple-500' },
  plugin: { label: 'Plugin', color: 'bg-green-500' },
  theme: { label: 'Theme', color: 'bg-amber-500' },
  option: { label: 'Option', color: 'bg-cyan-500' },
  post: { label: 'Post', color: 'bg-pink-500' },
}
